import { useMemo } from 'react';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useSuspenseQuery } from '@tanstack/react-query';
import { validateUIMessages, type ChatStatus, type UIMessage } from 'ai';
import {
  CONVERSATION_SYSTEM_MESSAGE_ID,
  MessageClass,
  toAiSdkCompatibleUIMessages,
  uiMessageFromPersistedSystemBody,
} from '@cotera/sdk/core';
import { Card } from './ui/card';
import { RenderMessage } from '../chat/message';
import type { ChatModel } from '../chat/chat.model';
import type { StackFrameType } from '@cotera/api';

interface ExecutionFlowDisplayProps {
  frames?: StackFrameType[];
}

/**
 * Extract chatId from frames by looking at stepChat frame outputs,
 * or extractObject frame input (for agents without tools).
 */
const extractChatIdFromFrames = (
  frames: StackFrameType[] | undefined
): string | null => {
  if (frames === undefined || frames.length === 0) {
    return null;
  }

  // First, try to find chatId in stepChat frame outputs
  const stepChatFrame = frames.find(
    (frame) =>
      frame.fpType === 'stepChat' &&
      (frame.output as { val?: { chatId?: string } } | null)?.val?.chatId
  );

  if (stepChatFrame !== undefined) {
    return (stepChatFrame.output as { val: { chatId: string } }).val.chatId;
  }

  // If no stepChat frames, check extractObject frame input
  // This happens when agents have no tools and skip the agent loop
  const extractObjectFrame = frames.find(
    (frame) => frame.fpType === 'extractObject'
  );

  if (extractObjectFrame !== undefined) {
    const chatId = (
      extractObjectFrame.input as {
        val?: { chatRef?: { chatId?: string } };
      } | null
    )?.val?.chatRef?.chatId;

    if (chatId !== undefined) {
      return chatId;
    }
  }

  return null;
};

const useChatMessages = (chatId: string | null) => {
  const client = useTenantedClient();
  return useSuspenseQuery({
    queryKey: ['chat-messages', chatId],
    queryFn: async (): Promise<UIMessage[]> => {
      if (chatId === null) {
        return [];
      }
      const result = await client.resource.chat.listMessages({
        conversationId: chatId,
      });
      if (result.isErr()) {
        return [];
      }
      const uiMessages = result.value.messages
        .map((row) => {
          const record = row as {
            role: string;
            message: string | Record<string, unknown>;
            messageId?: string | null;
            type?: string;
          };
          if (record.type === 'hidden') {
            return null;
          }
          const messageId = record.messageId ?? null;
          if (messageId === CONVERSATION_SYSTEM_MESSAGE_ID) {
            return null;
          }
          if (
            uiMessageFromPersistedSystemBody(messageId, record.message) !== null
          ) {
            return null;
          }
          const parsed =
            typeof record.message === 'string'
              ? (JSON.parse(record.message) as UIMessage)
              : (record.message as unknown as UIMessage);
          if (parsed.id === CONVERSATION_SYSTEM_MESSAGE_ID) {
            return null;
          }
          return parsed;
        })
        .filter((msg): msg is UIMessage => msg !== null);
      return validateUIMessages({
        messages: toAiSdkCompatibleUIMessages(uiMessages),
      });
    },
  });
};

/**
 * Minimal ChatModel implementation for readonly message display.
 * Only implements the methods needed by RenderMessage.
 */
class ReadonlyChatModel
  implements
    Pick<
      ChatModel,
      'lastMessageId' | 'status' | 'addToolResult' | 'id' | 'model'
    >
{
  constructor(readonly id: string, readonly messages: UIMessage[]) {}

  get lastMessageId(): string | undefined {
    return this.messages.at(-1)?.id;
  }

  get status(): ChatStatus {
    return 'ready';
  }

  get model(): string {
    return ''; // Default empty model for readonly display
  }

  addToolResult(_toolCallId: string, _result: unknown): void {
    // noop - readonly display doesn't handle tool results
  }
}

export const ExecutionFlowDisplay: React.FC<ExecutionFlowDisplayProps> = ({
  frames,
}) => {
  const chatId = extractChatIdFromFrames(frames);
  const { data: messages } = useChatMessages(chatId);
  const chatModel = useMemo(
    () => new ReadonlyChatModel(chatId ?? '', messages),
    [chatId, messages]
  );

  const messageInstances = useMemo(
    () => messages.map((msg) => MessageClass.fromUIMessage(chatId ?? '', msg)),
    [messages, chatId]
  );

  if (messages.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No chat history available
      </div>
    );
  }

  return (
    <Card className="p-3">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 rounded-full bg-green-500" />
        <span className="text-sm font-medium text-slate-700">
          Chat History ({messages.length} messages)
        </span>
      </div>
      <div className="space-y-2">
        {messageInstances.map((message) => (
          <RenderMessage
            key={message.id}
            message={message}
            chatModel={chatModel}
            readonly={true}
          />
        ))}
      </div>
    </Card>
  );
};
